import { FC, useMemo } from 'react';
import styled from '@emotion/styled';
import { useMutation } from '@apollo/client';
import { default as _PauseCircleOutlineIcon } from '@material-ui/icons/PauseCircleOutline';
import { default as _PlayCircleOutlineIcon } from '@material-ui/icons/PauseCircleOutline';

import {
  AdoptVariationDocument,
  AdoptVariationMutation,
  AdoptVariationMutationVariables,
  ChangeBaseVariationDocument,
  ChangeBaseVariationMutation,
  ChangeBaseVariationMutationVariables,
  PauseRoundVariationDocument,
  PauseRoundVariationMutation,
  PauseRoundVariationMutationVariables,
  ReserveVariationDocument,
  ReserveVariationMutation,
  ReserveVariationMutationVariables,
  ResumeRoundVariationDocument,
  ResumeRoundVariationMutation,
  ResumeRoundVariationMutationVariables,
  PossibleProject,
  PossibleVariation,
} from '../../../../generated/graphql';
import { SecondarySmallButton as SecondaryButton } from './common';
import mutationVariables from '../../../../utility/mutationVariables';

type Props = {
  project: PossibleProject;
  variation: PossibleVariation;
  isPausedVariation?: boolean;
  refetch: () => void;
};

const RoundStatusDisplay: FC<Props> = ({ project, variation, isPausedVariation, refetch }) => {
  const { permissions } = variation;

  const [changeBaseVariationMutation] = useMutation<ChangeBaseVariationMutation, ChangeBaseVariationMutationVariables>(
    ChangeBaseVariationDocument,
    {
      context: { clientName: 'planbcd' },
      onCompleted: refetch,
    },
  );

  const [reserveVariationMutation] = useMutation<ReserveVariationMutation, ReserveVariationMutationVariables>(
    ReserveVariationDocument,
    {
      context: { clientName: 'planbcd' },
      onCompleted: refetch,
    },
  );

  const [adoptVariationMutation] = useMutation<AdoptVariationMutation, AdoptVariationMutationVariables>(
    AdoptVariationDocument,
    {
      context: { clientName: 'planbcd' },
      onCompleted: refetch,
    },
  );

  const [pauseRoundVariationMutation] = useMutation<PauseRoundVariationMutation, PauseRoundVariationMutationVariables>(
    PauseRoundVariationDocument,
    {
      context: { clientName: 'planbcd' },
      onCompleted: refetch,
    },
  );

  const [resumeRoundVariationMutation] = useMutation<
    ResumeRoundVariationMutation,
    ResumeRoundVariationMutationVariables
  >(ResumeRoundVariationDocument, {
    context: { clientName: 'planbcd' },
    onCompleted: refetch,
  });

  const changeScoreBase = () =>
    changeBaseVariationMutation(
      mutationVariables({
        id: project?.id,
        variation: variation,
      }),
    );

  const canBecomeBase = useMemo(() => {
    if (variation?.reserved_score_base) {
      // 既にベースデザイン
      return false;
    }
    if (project?.state === 'competition') {
      // ラウンド計測中
      return false;
    }
    if (permissions.set_as_reserved_score_base?.permit) {
      // ベースデザインの変更を許可しない
      return false;
    }
    if (variation?.state !== 'reserved_for_competition') {
      // 次ラウンドで利用予定ではない
      return false;
    }
    return true;
  }, [variation]);

  const reserveForNextRound = () => reserveVariationMutation();
  const removeFromNextRound = () => adoptVariationMutation();
  const canReserveForNextRound = useMemo(() => {
    return variation?.state === 'adopted';
  }, [variation]);

  const pauseRoundVariation = () =>
    pauseRoundVariationMutation(
      mutationVariables({
        id: project?.id,
        variation: variation,
      }),
    );

  const resumeRoundVariation = () =>
    resumeRoundVariationMutation(
      mutationVariables({
        id: project?.id,
        variation: variation,
      }),
    );

  const canPause = useMemo(() => {
    if (project?.state !== 'competition') {
      // ラウンド計測中
      return false;
    }
    if (variation?.latest_round_variation === null) {
      // 最新ラウンドで使われてない
      return false;
    }
    if (variation?.latest_round_variation?.score_base === true) {
      // 最新ラウンドのベース
      return false;
    }
    if (isPausedVariation === true) {
      // すでに停止されている
      return false;
    }
    return true;
  }, []);

  return (
    <StyledPaperInner>
      {canBecomeBase && <SecondaryButton onClick={changeScoreBase}>ベースデザインにする</SecondaryButton>}
      {canReserveForNextRound && (
        <SecondaryButton onClick={reserveForNextRound}>次のラウンドで使用する</SecondaryButton>
      )}
      {!canReserveForNextRound && (
        <SecondaryButton onClick={removeFromNextRound}>次のラウンドで使用しない</SecondaryButton>
      )}
      {canPause && (
        <SecondaryButton onClick={pauseRoundVariation}>
          <PauseCircleOutlineIcon />
          <div>停止する</div>
        </SecondaryButton>
      )}
      {isPausedVariation && (
        <SecondaryButton onClick={resumeRoundVariation}>
          <PauseCircleOutlineIcon />
          <div>再開する</div>
        </SecondaryButton>
      )}
    </StyledPaperInner>
  );
};

const StyledPaperInner = styled('div')(`
  width: 100%;  
  height: 69px;
  padding: 0 1rem;
  margin-top: 16px;
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-direction: row;
  justify-content: flex-end;
  background: #F5F6F7;
  box-shadow: 0px 1px 9px rgba(0, 0, 0, 0.04);
  border-radius: 8px;
`);

const PauseCircleOutlineIcon = styled(_PauseCircleOutlineIcon)(`
  margin-right: 0.4rem;
`);

export default RoundStatusDisplay;
